import { useState } from 'react'
import { Redacted, Reveal } from '../primitives'
import { SharingPermKey, visibleFields } from '../wizard/wizardData'

interface Audience {
  id: string
  name: string
  perms: Record<SharingPermKey, boolean>
}

const AUDIENCES: Audience[] = [
  {
    id: 'forum',
    name: 'The forum thread',
    perms: { gps: false, campsites: false, route: true, crew: false, notes: false },
  },
  {
    id: 'friend',
    name: 'A friend of a friend',
    perms: { gps: false, campsites: false, route: true, crew: true, notes: true },
  },
  {
    id: 'crew',
    name: 'The rigs actually coming',
    perms: { gps: true, campsites: true, route: true, crew: true, notes: true },
  },
]

const DEMO_MILES = 199

export function SharingSection() {
  const [audienceId, setAudienceId] = useState(AUDIENCES[0].id)
  const audience = AUDIENCES.find((a) => a.id === audienceId) ?? AUDIENCES[0]
  const fields = visibleFields(audience.perms, { totalMiles: DEMO_MILES })

  return (
    <section id="sharing" className="l-anchor pt-[var(--l-section)]">
      <div className="l-frame grid gap-12 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-5">
          <Reveal as="h2" className="l-head">
            Share on your terms.
          </Reveal>
          <Reveal as="p" delay={60} className="l-soft mt-4 max-w-[44ch]">
            Every link has its own permissions. Change the plan once and each audience sees what you allow, nothing
            more. Audience changed? Kill the link.
          </Reveal>

          <Reveal delay={120} className="mt-10">
            <ul aria-label="Who's looking?">
              {AUDIENCES.map((option) => (
                <li key={option.id}>
                  <button
                    type="button"
                    className="l-option"
                    aria-pressed={option.id === audience.id}
                    onClick={() => setAudienceId(option.id)}
                  >
                    {option.name}
                  </button>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal as="p" delay={180} className="l-soft l-small mt-8">
            Per-link controls are Pro. Basic sharing is free.
          </Reveal>
        </div>

        {/* The shared trip, as this audience receives it */}
        <Reveal delay={120} className="bg-landing-surface p-5 sm:p-8 lg:col-span-6 lg:col-start-7 lg:self-start">
          <h3 className="font-medium">Mojave Spring Run</h3>
          <p className="l-soft l-small">Apr 12–15, 4 days</p>
          <dl className="mt-6 grid gap-x-8 gap-y-3 sm:grid-cols-[minmax(0,11em)_minmax(0,1fr)]">
            {fields.map((field, index) => (
              <div key={field.key} className="contents">
                <dt className="l-soft l-small mt-2 sm:mt-0 sm:leading-[1.65]">{field.label}</dt>
                <dd className="min-w-0">
                  <Redacted hidden={!field.visible} hiddenLabel={field.hiddenValue} index={index}>
                    {field.value}
                  </Redacted>
                </dd>
              </div>
            ))}
          </dl>
        </Reveal>
      </div>
    </section>
  )
}
